import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const FontContext = createContext();

// Self-hosted fonts that don't need Google Fonts loading
const SELF_HOSTED_FONTS = ['Press Start 2P', 'Pacifico', 'Poppins'];

// System fonts that are always available
const SYSTEM_FONTS = ['Inter', 'system-ui', 'Arial', 'Helvetica'];

/**
 * Inject a Google Fonts CSS v2 stylesheet link and wait for it to load.
 * Returns true when the stylesheet is ready, false on network error.
 */
const injectGoogleCSS = (fontName) => {
  const linkId = `gfont-${fontName.replace(/\s+/g, '-').toLowerCase()}`;
  const existing = document.getElementById(linkId);
  if (existing) return Promise.resolve(true);

  return new Promise((resolve) => {
    const link = document.createElement('link');
    link.id = linkId;
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(fontName)}:wght@400;700&display=swap`;
    link.onload = () => resolve(true);
    link.onerror = () => resolve(false);
    document.head.appendChild(link);
  });
};

/**
 * FontProvider - Centralized font loading management
 * Uses native CSS Font Loading API + Google Fonts CSS v2
 * Prevents duplicate font loading across components
 */
export const FontProvider = ({ children }) => {
  const [loadedFonts, setLoadedFonts] = useState(new Set(['Inter']));
  const [loadingFonts, setLoadingFonts] = useState(new Set());
  const loadPromises = useRef(new Map());

  /**
   * Load a Google Font if not already loaded
   * Returns a promise that resolves when the font is ready
   */
  const loadFont = useCallback((fontName) => {
    // Skip if no font, already loaded, or system/self-hosted font
    if (!fontName ||
        loadedFonts.has(fontName) ||
        SYSTEM_FONTS.includes(fontName) ||
        SELF_HOSTED_FONTS.includes(fontName)) {
      return Promise.resolve(true);
    }

    // Return existing promise if already loading
    if (loadPromises.current.has(fontName)) {
      return loadPromises.current.get(fontName);
    }

    // Create new loading promise using native CSS Font Loading API
    const promise = (async () => {
      setLoadingFonts(prev => new Set([...prev, fontName]));

      try {
        // 1. Inject Google Fonts CSS v2 stylesheet
        const cssOk = await injectGoogleCSS(fontName);
        if (!cssOk) throw new Error('stylesheet failed');

        // 2. Trigger font file download and wait for availability
        await document.fonts.load(`16px "${fontName}"`);
        const ready = document.fonts.check(`16px "${fontName}"`);

        if (ready) {
          setLoadedFonts(prev => new Set([...prev, fontName]));
          return true;
        }
        throw new Error('font not available');
      } catch {
        console.warn(`Font failed to load: ${fontName}`);
        // Mark to prevent retry loops
        setLoadedFonts(prev => new Set([...prev, fontName]));
        return false;
      } finally {
        setLoadingFonts(prev => {
          const next = new Set(prev);
          next.delete(fontName);
          return next;
        });
        loadPromises.current.delete(fontName);
      }
    })();

    loadPromises.current.set(fontName, promise);
    return promise;
  }, [loadedFonts]);

  /**
   * Check if a font is loaded and ready to use
   */
  const isFontLoaded = useCallback((fontName) => {
    if (!fontName || SYSTEM_FONTS.includes(fontName) || SELF_HOSTED_FONTS.includes(fontName)) {
      return true;
    }
    return loadedFonts.has(fontName);
  }, [loadedFonts]);

  /**
   * Check if a font is currently being loaded
   */
  const isFontLoading = useCallback((fontName) => {
    return loadingFonts.has(fontName);
  }, [loadingFonts]);

  /**
   * Preload multiple fonts at once
   */
  const preloadFonts = useCallback((fontNames) => {
    const uniqueFonts = [...new Set(fontNames)].filter(Boolean);
    return Promise.all(uniqueFonts.map(loadFont));
  }, [loadFont]);

  const value = {
    loadFont,
    isFontLoaded,
    isFontLoading,
    preloadFonts,
    loadedFonts: [...loadedFonts],
    SELF_HOSTED_FONTS,
    SYSTEM_FONTS
  };

  return (
    <FontContext.Provider value={value}>
      {children}
    </FontContext.Provider>
  );
};

/**
 * Hook to access font loading context
 */
export const useFont = () => {
  const context = useContext(FontContext);
  if (!context) {
    // Fallback for components rendered outside provider (e.g., Play page)
    return {
      loadFont: () => Promise.resolve(true),
      isFontLoaded: () => true,
      isFontLoading: () => false,
      preloadFonts: () => Promise.resolve([]),
      loadedFonts: ['Inter'],
      SELF_HOSTED_FONTS,
      SYSTEM_FONTS
    };
  }
  return context;
};

export default FontContext;
